/**
 * FO-391 (batch-46) - Rekenkern voor de jaarlijkse tarief-indexatie.
 *
 * `berekenIndexatie(oud, pct)` = oud x (1 + pct/100), EXACT doorgerekend als
 * decimaal (geen float) en pas op het EINDBEDRAG afgerond op 2 decimalen,
 * half-up (weg van nul, gelijk aan Prisma/decimal.js ROUND_HALF_UP).
 *
 * Bewust dependency-vrij (`@streep/utils` blijft licht): de decimaal-rekenkern
 * werkt op BigInt-mantissen, zodat er geen precisie verloren gaat en utils geen
 * decimal.js/Prisma-Decimal hoeft te importeren. De API converteert aan de rand:
 * `berekenIndexatie(oud.toString(), pct.toString())` -> `new Prisma.Decimal(res)`.
 *
 * GELD-PAD: EEN bron voor de afrondingsregel. Zowel de preview als het toepassen
 * roepen deze functie aan; ze kunnen dus nooit uit elkaar lopen.
 */

interface ParsedDecimal {
  /** true = negatief teken in de invoer (bedragen zijn normaal positief). */
  neg: boolean
  /** Mantisse zonder teken (waarde = digits / 10^scale). */
  digits: bigint
  /** Aantal decimalen. */
  scale: number
}

// BigInt-constanten via `BigInt(...)` i.p.v. literals (`0n`), zodat dit bestand
// ook compileert onder de lagere TS-target van de frontend-apps die `@streep/utils`
// importeren (BigInt-literals vereisen target >= ES2020).
const ZERO = BigInt(0)
const ONE = BigInt(1)
const TWO = BigInt(2)
const TEN = BigInt(10)
const HUNDRED = BigInt(100)

function parseDecimal(input: string, veld: string): ParsedDecimal {
  const s = input.trim()
  const m = /^(-)?(\d+)(?:\.(\d+))?$/.exec(s)
  if (!m) {
    throw new Error(`Ongeldig decimaal getal voor ${veld}: "${input}"`)
  }
  const neg = m[1] === '-'
  const intPart = m[2] ?? '0'
  const fracPart = m[3] ?? ''
  const digits = BigInt(intPart + fracPart)
  return { neg, digits, scale: fracPart.length }
}

function pow10(n: number): bigint {
  return TEN ** BigInt(n)
}

/**
 * Rond een niet-negatieve mantisse (op schaal `scale`) half-up (weg van nul) af
 * naar 2 decimalen en geef de mantisse op schaal 2 terug (= waarde x 100).
 */
function roundHalfUpTo2(mantissa: bigint, scale: number): bigint {
  if (scale <= 2) {
    return mantissa * pow10(2 - scale)
  }
  const divisor = pow10(scale - 2)
  const q = mantissa / divisor
  const r = mantissa % divisor
  // Half-up (weg van nul): rest >= 0,5 eenheid -> naar boven.
  return TWO * r >= divisor ? q + ONE : q
}

/** Formatteer een schaal-2-mantisse (waarde x 100) naar een vaste-2-decimalen string. */
function formatFixed2(neg: boolean, cents: bigint): string {
  const whole = cents / HUNDRED
  const frac = cents % HUNDRED
  const fracStr = frac.toString().padStart(2, '0')
  const sign = neg && cents !== ZERO ? '-' : ''
  return `${sign}${whole.toString()}.${fracStr}`
}

/**
 * Normaliseer een decimaal-string naar exact 2 decimalen (half-up). Handig voor
 * de "oud"-kolom in preview/export zodat oud en nieuw hetzelfde formaat hebben.
 */
export function naarTweeDecimalen(waarde: string): string {
  const p = parseDecimal(waarde, 'waarde')
  const cents = roundHalfUpTo2(p.digits, p.scale)
  return formatFixed2(p.neg, cents)
}

/**
 * Bereken het geindexeerde bedrag: `oud x (1 + pct/100)`, afgerond op 2
 * decimalen (half-up), afronding UITSLUITEND op het eindbedrag.
 *
 * @param oud - bedrag als decimaal-string (bijv. "70.15")
 * @param pct - indexatiepercentage als decimaal-string (bijv. "3.5" voor 3,5%)
 * @returns het nieuwe bedrag als string met exact 2 decimalen (bijv. "72.61")
 */
export function berekenIndexatie(oud: string, pct: string): string {
  const o = parseDecimal(oud, 'oud bedrag')
  const p = parseDecimal(pct, 'percentage')

  // factor = 1 + pct/100 = (10^(pScale+2) +/- pctMantisse) / 10^(pScale+2)
  const factorScale = p.scale + 2
  const signedFactor = pow10(factorScale) + (p.neg ? -p.digits : p.digits)
  const factorNeg = signedFactor < ZERO
  const factorMantisse = factorNeg ? -signedFactor : signedFactor

  // product = oud x factor (mantissen vermenigvuldigen, schalen optellen)
  const prodNeg = o.neg !== factorNeg
  const prodMantisse = o.digits * factorMantisse
  const prodScale = o.scale + factorScale

  const cents = roundHalfUpTo2(prodMantisse, prodScale)
  return formatFixed2(prodNeg, cents)
}
